import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import MainActions from "@/components/MainActions";
import ChoiceChips from "@/components/ChoiceChips";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFCard from "@/components/Card";
import {PlayerAPI} from "@/api/player";

import SaveIcon from "@mui/icons-material/Save";

import {
	dateFormat,
	vasOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	REQUIRED_FIELD,
} from "@/utils/constants";
import {checkRequiredFields} from "@/utils/functions";

import "@/pages/TQR/index.scss";

const FIELD_DATE_FORMAT = "YYYY-MM-DD";

const requiredFields = ["vasValue"];

const STAFF_PERIODS = [
	{label: "7 giorni", days: 7},
	{label: "14 giorni", days: 14},
	{label: "30 giorni", days: 30},
];

const PLAYER_PERIODS = [
	{label: "7 giorni", days: 7},
	{label: "30 giorni", days: 30},
	{label: "3 mesi", days: 90},
];

function VASPage(props) {
	const {handleNotification} = props;

	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const isPlayer = userRole === "Player";

	const periods = isPlayer ? PLAYER_PERIODS : STAFF_PERIODS;
	const defaultDays = isPlayer ? 30 : 7;

	const [activePeriod, setActivePeriod] = useState(defaultDays);
	const [loading, setLoading] = useState(false);
	const [vas, setVas] = useState([]);

	// Inline add form (player only)
	const [addFields, setAddFields] = useState({});
	const [addLoading, setAddLoading] = useState(false);

	// Edit / delete
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openHandleEdit, setOpenHandleEdit] = useState(false);
	const [editValue, setEditValue] = useState();
	const [currentVAS, setCurrentVAS] = useState({});
	const [editErrors, setEditErrors] = useState([]);

	const filtersData = {
		fromDate: moment()
			.subtract(activePeriod - 1, "days")
			.format(FIELD_DATE_FORMAT),
		toDate: moment().format(FIELD_DATE_FORMAT),
	};

	const getVAS = async () => {
		setLoading(true);
		try {
			const response = isPlayer
				? await PlayerAPI.playerListVAS(playerId, filtersData)
				: await PlayerAPI.listVAS(filtersData);
			setVas(response || []);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const saveVAS = async () => {
		setAddLoading(true);
		try {
			await PlayerAPI.createVAS({
				playerId,
				vasDate: moment(addFields.date).format(FIELD_DATE_FORMAT),
				vasValue: addFields.value?.value,
			});
			setAddFields({});
			getVAS();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setAddLoading(false);
		}
	};

	const deleteVAS = async () => {
		setLoading(true);
		try {
			await PlayerAPI.deleteVAS(
				isPlayer ? playerId : currentVAS?.playerId,
				currentVAS?.id,
			);
			getVAS();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const updateVAS = async () => {
		setLoading(true);
		try {
			await PlayerAPI.updateVAS(
				isPlayer ? playerId : currentVAS?.playerId,
				currentVAS?.id,
				{...currentVAS, vasValue: editValue?.value},
			);
			getVAS();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleEdit(false);
		}
	};

	useEffect(() => {
		getVAS();
	}, [activePeriod]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData.fromDate,
			setValue: () => {},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData.toDate,
			setValue: () => {},
		},
	];

	const columns = [
		{
			label: "Data",
			key: "date",
			customRender: (item) => moment(item.vasDate).format(dateFormat),
			small: true,
		},
	];

	if (!isPlayer) {
		columns.push({
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		});
	}

	columns.push({
		label: "VAS",
		key: "vas",
		customRender: (item) =>
			vasOptions.find((o) => o.value === item.vasValue)?.label ?? "---",
		align: "start",
	});

	const handleEdit = (item) => {
		setEditValue({
			value: item.vasValue,
			label: vasOptions.find((o) => o.value === item.vasValue)?.label,
		});
		setCurrentVAS(item);
		setOpenHandleEdit(true);
	};

	const listConfiguration = {
		data: vas,
		handleEdit,
		handleDelete: (item) => {
			setCurrentVAS(item);
			setOpenHandleConfirm(true);
		},
	};

	return (
		<Page>
			<Loading visible={loading || addLoading} />

			<Breadcrumbs current="VAS" />

			{/* ── Player: quick-add inline form ── */}
			{isPlayer && (
				<div className="meas-quick-add">
					<VLFCard
						title="Hai dolore? (VAS)"
						openable={false}
						content={
							<div className="meas-quick-add__fields">
								<div className="meas-quick-add__field">
									<VLFDatePicker
										label="Data"
										value={addFields.date}
										onChange={(v) => setAddFields({...addFields, date: v})}
										required
										size="small"
									/>
								</div>
								<div className="meas-quick-add__field">
									<VLFSelectNew
										options={vasOptions}
										value={addFields.value || ""}
										onChange={(v) => setAddFields({...addFields, value: v})}
										label="Intensità dolore"
										sorted={false}
										required
									/>
								</div>
								<MainActions
									actions={[
										{
											icon: <SaveIcon />,
											label: "Salva",
											onClick: saveVAS,
											disabled: !addFields.date || !addFields.value,
										},
									]}
								/>
							</div>
						}
					/>
				</div>
			)}

			<ChoiceChips
				options={periods.map((p) => ({value: p.days, label: p.label}))}
				value={activePeriod}
				onChange={setActivePeriod}
			/>

			<CardsContainer
				tableComponent={
					<CardsTable
						filtersData={filters}
						configurationColumns={columns}
						data={vas}
						rowActions
						handleDelete={(item) => {
							setCurrentVAS(item);
							setOpenHandleConfirm(true);
						}}
						handleEdit={handleEdit}
						defaultSortedField="vasDate"
						defaultSortedOrder="descend"
					/>
				}
				listComponent={
					<CardsList
						type="vas"
						breadcrumbs
						filtersData={filters}
						listConfiguration={listConfiguration}
						defaultSortedOrder="descend"
					/>
				}
			/>

			{openHandleConfirm && (
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={deleteVAS}
					confirmLabel="Conferma"
					close={() => setOpenHandleConfirm(false)}
				/>
			)}

			{openHandleEdit && (
				<VLFModal
					open={openHandleEdit}
					title="Modifica valore"
					content={
						<div className="row">
							<div className="col-12">
								<VLFSelectNew
									options={vasOptions}
									value={editValue || ""}
									onChange={(v) => {
										setEditErrors([]);
										setEditValue(v);
									}}
									required
									label="VAS"
									sorted={false}
									helperText={
										editErrors.includes("vasValue") ? REQUIRED_FIELD : ""
									}
									error={editErrors.includes("vasValue")}
								/>
							</div>
						</div>
					}
					confirm={() => {
						const errors = checkRequiredFields(requiredFields, {
							vasValue: editValue,
						});
						setEditErrors(errors);
						if (!errors.length) updateVAS();
					}}
					confirmLabel="Conferma"
					close={() => setOpenHandleEdit(false)}
				/>
			)}
		</Page>
	);
}

export default VASPage;
